import { applyPatch } from "fast-json-patch";
import { patchReplace } from "../../helpers/patchDoc";
import { wealthProApi } from "../../redux/api";
import { useCacheKeyMergeHandler, useCacheKeySerializeQueryArgsHandler, useCacheKeyTransformResponseHandler } from "../../redux/helpers";

const userMessagesApiEndpoints = wealthProApi.enhanceEndpoints({
    addTagTypes: ['ProjectMessages']
}).injectEndpoints({
    endpoints: (builder) => ({
        createMessageForUser: builder.mutation({
            query: ({ userId, ...request }) => ({
                url: `api/messaging/users/${userId}/messages`,
                method: 'POST',
                body: {
                    statusText: "",
                    isDone: false,
                    message: "",
                    threadSubject: "",
                    recipientIds: [],
                    ...request,
                },
            }),
            async onQueryStarted({ userId, threadId }, { dispatch, queryFulfilled }) {
                try {
                    const { data: message } = await queryFulfilled;
                    dispatch(wealthProApi.util.updateQueryData('searchUserMessages', null, ({ results, pagination, ...rest }) => {
                        return {
                            ...rest,
                            results: [message, ...results],
                            pagination: {
                                ...pagination,
                                totalCount: pagination.totalCount + 1
                            }
                        }
                    }));

                    dispatch(wealthProApi.util.updateQueryData('fetchThreadMessagesForUser', { userId, threadId }, ({ results, totalCount, ...rest }) => {
                        return {
                            ...rest,
                            results: [{
                                ...message,
                                recipients: message.recipientIds.map(userId => ({ userId }))
                            }, ...results],
                            totalCount: totalCount + 1
                        }
                    }));
                }
                catch (e) {
                    console.error('createMessageForUser', e);
                }
            }
        }),
        createMessageWithAttachmentsForUser: builder.mutation({
            query: ({ userId, request }) => ({
                url: `api/messaging/users/${userId}/messages/withAttachments`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ userId, threadId }, { dispatch, queryFulfilled }) {
                try {
                    const { data: message } = await queryFulfilled;
                    dispatch(wealthProApi.util.updateQueryData('searchUserMessages', null, ({ results, ...rest }) => {
                        return {
                            results: [message, ...results],
                            ...rest
                        }
                    }));

                    dispatch(wealthProApi.util.updateQueryData('fetchThreadMessagesForUser', { userId, threadId }, ({ results, totalCount, ...rest }) => {
                        return {
                            ...rest,
                            results: [{
                                ...message,
                                recipients: message.recipientIds.map(userId => ({ userId }))
                            }, ...results],
                            totalCount: totalCount + 1
                        }
                    }));
                }
                catch (e) {
                    console.error('createMessageWithAttachmentsForUser', e);
                }
            }
        }),
        uploadMessageAttachmentsForUser: builder.mutation({
            query: ({ request }) => ({
                url: `api/messaging/messages/attachments`,
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ userId, messageId, threadId }, { dispatch, queryFulfilled }) {
                try {
                    const { data: newAttachments } = await queryFulfilled;
                    dispatch(wealthProApi.util.updateQueryData('fetchThreadMessagesForUser', { userId, threadId }, ({ results }) => {
                        let item = results.find(x => x.messageId === messageId);
                        item.attachments = [...item.attachments, ...newAttachments];
                    }));

                    dispatch(wealthProApi.util.updateQueryData('getThreadById', { threadId }, (thread) => {
                        thread.threadAttachments = [...thread.threadAttachments, ...newAttachments]
                    }));
                }
                catch (e) {
                    console.error('uploadMessageAttachmentsForUser', e);
                }
            }
        }),
        searchUserMessages: builder.query({
            query: ({ userId, request }) => ({
                url: `api/messaging/users/${userId}/messages/search`,
                method: 'POST',
                body: request
            }),
            transformResponse: useCacheKeyTransformResponseHandler,
            serializeQueryArgs: useCacheKeySerializeQueryArgsHandler,
            merge: useCacheKeyMergeHandler,
        }),
        readUserMessage: builder.mutation({
            query: ({ userId, messageId, request }) => ({
                url: `api/messaging/users/${userId}/messages/${messageId}/read`,
                method: 'POST',
                body: request
            }),
            async onQueryStarted({ messageId, userId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(wealthProApi.util.updateQueryData('searchUserMessages', null, ({ results, pagination, ...rest }) => {
                        if (data.userNoFurtherActionTime) {
                            return {
                                ...rest,
                                pagination: {
                                    ...pagination,
                                    totalCount: pagination.totalCount - 1
                                },
                                results: results.filter(x => x.messageId !== messageId)
                            };
                        }
                        let message = results.find(x => x.messageId === messageId);
                        if (message) {
                            Object.assign(message, data);
                        }
                    }));
                }
                catch (e) {
                    console.error('readUserMessage', e);
                }
            },
            invalidatesTags: ['ProjectMessages']
        }),
        unreadUserMessage: builder.mutation({
            query: ({ userId, messageId, request }) => ({
                url: `api/messaging/users/${userId}/messages/${messageId}/unread`,
                method: 'POST',
                body: request
            }),
            async onQueryStarted({ messageId, userId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(wealthProApi.util.updateQueryData('searchUserMessages', null, ({ results }) => {
                        let message = results.find(x => x.messageId === messageId);
                        if (message) {
                            Object.assign(message, data);
                        }
                    }));
                }
                catch (e) {
                    console.error('unreadUserMessage', e);
                }
            },
            invalidatesTags: ['ProjectMessages']
        }),
        sendUserMessage: builder.mutation({
            query: ({ userId, messageId }) => ({
                url: `api/messaging/users/${userId}/messages/${messageId}/send`,
                method: 'POST'
            }),
            async onQueryStarted({ userId, threadId, messageId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(wealthProApi.util.updateQueryData('searchUserMessages', null, ({ results, pagination: { totalCount, ...pagedRest }, ...rest }) => {
                        let filteredResults = results.filter(x => x.messageId !== messageId);
                        if (filteredResults) {
                            return {
                                ...rest,
                                pagination: {
                                    ...pagedRest,
                                    totalCount,
                                },
                                results: [{
                                    ...data,
                                    recipientNames: data.recipients.map(({ displayName }) => displayName),
                                    recipientIds: data.recipients.map(({ userId }) => userId),
                                    subject: data.threadSubject,
                                    userMessagePriority: null,
                                    deadlineDate: null,
                                    userReminderDate: null

                                }, ...filteredResults]
                            };
                        }
                    }));

                    dispatch(wealthProApi.util.updateQueryData('fetchThreadMessagesForUser', { userId, threadId }, ({ results, totalCount, ...rest }) => {
                        let filteredResults = results.filter(x => x.messageId !== messageId);
                        if (filteredResults) {
                            return {
                                ...rest,
                                totalCount,
                                results: [data, ...filteredResults]
                            };
                        }
                    }));
                }
                catch (e) {
                    console.error('sendUserMessage', e);
                }
            },
        }),
        toggleMessageIsDoneForUser: builder.mutation({
            query: ({ userId, messageId, newValue }) => ({
                url: `api/messaging/users/${userId}/messages/${messageId}/done`,
                method: 'PATCH',
                body: {
                    isDone: newValue
                }
            }),
            async onQueryStarted({ messageId, userId, threadId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const operations = [
                        patchReplace("isDone", data.newValue),
                        patchReplace("doneDate", data.doneDate),
                        patchReplace("doneByName", data.toggledByName),
                        patchReplace("doneById", data.toggledBy)
                    ];

                    dispatch(wealthProApi.util.updateQueryData('searchUserMessages', null, ({ results }) => {
                        let message = results.find(x => x.messageId === messageId);
                        if (message) {
                            applyPatch(message, operations, true);
                        }
                    }));

                    dispatch(wealthProApi.util.updateQueryData('fetchThreadMessagesForUser', { userId, threadId }, ({ results }) => {
                        let message = results.find(x => x.messageId === messageId);
                        if (message) {
                            applyPatch(message, operations, true);
                        }

                    }));
                }
                catch (e) {
                    console.error('toggleMessageIsDoneForUser', e);
                }
            }
        })
    })
});

export const {
    useCreateMessageForUserMutation,
    useCreateMessageWithAttachmentsForUserMutation,
    useUploadMessageAttachmentsForUserMutation,
    useLazySearchUserMessagesQuery,
    useReadUserMessageMutation,
    useUnreadUserMessageMutation,
    useSendUserMessageMutation,
    useToggleMessageIsDoneForUserMutation
} = userMessagesApiEndpoints;